import React, { useCallback, useEffect, useState } from 'react';
import { readPhaseGate, advanceAgentPhase } from '../../services/protectBuild';
import { LABEL, NOTE } from './typeScale';

/**
 * Moving the agent on, with the gate that decides it shown first.
 *
 * <h3>The gap this closes</h3>
 *
 * <p>Nothing in this walk ever advanced an agent's phase. The lifecycle calls
 * were wired into the console's pages and the v2 detail tabs and into nothing
 * on this surface, so an agent built here stayed in `build` for good and the
 * Execution-Ready gate's `phase_act` check could never pass. Observed live:
 * readiness down to two failing checks with no step here able to clear either.</p>
 *
 * <h3>One component, both steps</h3>
 *
 * <p>Build → Decide and Decide → Act differ only in which gate is previewed and
 * which lifecycle call commits it, and both are decided by the platform. Two
 * components would be two places for the same mistake.</p>
 *
 * <h3>Why the checks are the platform's sentences</h3>
 *
 * <p>Each check arrives as {check, passed, message} and the message is rendered
 * as sent. The alternative — a label map on this side — is what the Decide tab
 * shipped with: it carried only the keys its author had in mind, so every other
 * key fell through and a tenant read raw identifiers as their remediation list.</p>
 */
export default function PhaseAdvanceAsk({
  agentId, registrationId, fromPhase, toLabel, onAnswer, busy = false,
}) {
  const [gate, setGate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [problem, setProblem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setGate(await readPhaseGate(agentId, fromPhase));
    setLoading(false);
  }, [agentId, fromPhase]);
  useEffect(() => { load(); }, [load]);

  const advance = async () => {
    setWorking(true);
    setProblem(null);
    try {
      const res = await advanceAgentPhase(registrationId, fromPhase);
      if (!res || res.ok === false) {
        setProblem(res?.error || 'The platform would not advance this agent.');
        /* Re-read: a refusal means the gate moved under us, and the stale
           checklist above would still be showing it as clear. */
        await load();
        return;
      }
      onAnswer({ advancedTo: toLabel });
    } catch (e) {
      setProblem(e?.message || 'Could not advance this agent.');
    } finally {
      setWorking(false);
    }
  };

  if (loading) return <p style={NOTE}>Reading its gate…</p>;

  /* A gate that could not be read is not a gate that passed — offering the
     advance here would send somebody into a refusal with no checklist. */
  if (gate?.error) {
    return (
      <div style={COL}>
        <p style={{ ...NOTE, color: 'var(--p7-deny-ink)' }}>{gate.error}</p>
        <div><button type="button" className="p7-btn" onClick={load}>Try again</button></div>
      </div>
    );
  }

  const checks = gate?.checks || [];
  const unmet = checks.filter((c) => !c.passed);

  /* Already past this gate. Found live: the agent had advanced to Decide and
     this step still drew a red Build gate with "clear the step before it
     first" and a disabled button — no way onward, for any agent that had ever
     advanced. The gate reads red precisely BECAUSE it was cleared: the skills
     that qualified moved on with the agent, so nothing eval-ready is left in
     Build. Presenting those checks as blockers sends somebody to fix
     something that already worked. */
  if (gate?.alreadyPast) {
    return (
      <div style={COL}>
        <div style={PANEL}>
          <p style={{ ...LABEL, margin: 0 }}>Already moved to {toLabel}</p>
          <p style={{ ...NOTE, marginTop: '4px' }}>
            This agent is in {gate.currentPhase || toLabel.toLowerCase()} now, so there is
            nothing to advance here. The skills that qualified moved with it; any that did
            not are still behind, and can be measured and advanced on their own.
          </p>
        </div>
        <div>
          <button type="button" className="p7-btn p7-btn--primary" disabled={busy}
                  onClick={() => onAnswer({ advancedTo: toLabel })}>
            Continue
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={COL}>
      <div style={PANEL}>
        {checks.length === 0 ? (
          <p style={{ ...NOTE, margin: 0 }}>
            The platform reported no checks for this step, so there is nothing to show
            and nothing to clear. Nothing will be advanced from here.
          </p>
        ) : (
          <ul style={LIST}>
            {checks.map((c) => (
              <li key={c.id} style={ITEM}>
                <span aria-hidden="true" style={c.passed ? TICK : CROSS}>
                  {c.passed ? '✓' : '✗'}
                </span>
                <span style={{ ...NOTE, color: c.passed ? 'var(--p7-ink)' : 'var(--p7-deny-ink)' }}>
                  {c.detail || c.id}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      {problem ? (
        <p style={{ ...NOTE, color: 'var(--p7-deny-ink)' }}>{problem}</p>
      ) : null}

      {!gate?.actionable && checks.length > 0 ? (
        <p style={NOTE}>
          This agent is not standing at this gate yet, so there is nothing to advance
          from here. Clear the step before it first.
        </p>
      ) : null}

      <div>
        <button type="button" className="p7-btn p7-btn--primary"
                disabled={busy || working || !registrationId
                  || checks.length === 0 || unmet.length > 0 || !gate?.actionable}
                onClick={advance}>
          {working ? `Moving it to ${toLabel}…` : `Move it to ${toLabel}`}
        </button>
      </div>
    </div>
  );
}

const COL = { display: 'flex', flexDirection: 'column', gap: 'var(--p7-space-3)' };
const LIST = { listStyle: 'none', margin: 0, padding: 0 };
const ITEM = { display: 'flex', gap: '8px', alignItems: 'flex-start', marginBottom: '7px' };
const TICK = { ...LABEL, color: 'var(--p7-allow-ink)' };
const CROSS = { ...LABEL, color: 'var(--p7-deny-ink)' };
const PANEL = {
  border: '1px solid var(--p7-line)', borderRadius: 'var(--p7-radius-control)',
  padding: '12px 13px', background: 'var(--p7-surface-sunken)',
};
